<script setup>
const props = defineProps({
  status: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v)
  }
})

const STATUS_MAP = {
  // Stock
  IN_STOCK:              { label: 'En stock',         classes: 'bg-teal/10 text-teal border border-teal/25' },
  LOW_STOCK:             { label: 'Stock faible',      classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  OUT_OF_STOCK:          { label: 'Rupture',           classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  DISPONIBLE:            { label: 'En stock',          classes: 'bg-teal/10 text-teal border border-teal/25' },
  FAIBLE:                { label: 'Stock faible',      classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  RUPTURE:               { label: 'Rupture',           classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  // Generic positive
  APPROVED:              { label: 'Approuvé',          classes: 'bg-teal/10 text-teal border border-teal/25' },
  VALIDATED:             { label: 'Validé',            classes: 'bg-teal/10 text-teal border border-teal/25' },
  COMPLETED:             { label: 'Terminé',           classes: 'bg-teal/10 text-teal border border-teal/25' },
  ACTIVE:                { label: 'Actif',             classes: 'bg-teal/10 text-teal border border-teal/25' },
  CONVERTED:             { label: 'Convertie',         classes: 'bg-teal/10 text-teal border border-teal/25' },
  RECEIVED:              { label: 'Reçu',              classes: 'bg-teal/10 text-teal border border-teal/25' },
  SENT:                  { label: 'Envoyé',            classes: 'bg-blue-100 text-blue-700 border border-blue-200' },
  SELECTED:              { label: 'Sélectionné',       classes: 'bg-teal/10 text-teal border border-teal/25' },
  // Pending
  PENDING:               { label: 'En attente',        classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  PENDING_VALIDATION:    { label: 'En validation',     classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  PENDING_APPROVAL:      { label: 'En approbation',    classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  IN_TRANSIT:            { label: 'En transit',        classes: 'bg-blue-100 text-blue-700 border border-blue-200' },
  IN_PROGRESS:           { label: 'En cours',          classes: 'bg-blue-100 text-blue-700 border border-blue-200' },
  PREPARED:              { label: 'Préparé',           classes: 'bg-blue-100 text-blue-700 border border-blue-200' },
  // Negative
  REJECTED:              { label: 'Rejeté',            classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  CANCELLED:             { label: 'Annulé',            classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  REFUSED:               { label: 'Refusé',            classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  // Expiration
  EXPIRED:               { label: 'Expiré',            classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  EXPIRING_SOON:         { label: 'Expire bientôt',    classes: 'bg-orange-400/10 text-orange-600 border border-orange-400/25' },
  // Neutral
  DRAFT:                 { label: 'Brouillon',         classes: 'bg-offwhite-dim text-navy/60 border border-[#D0CABC]' },
  INACTIVE:              { label: 'Inactif',           classes: 'bg-offwhite-dim text-navy/60 border border-[#D0CABC]' },
  // Exits
  VALIDATED_EXIT:        { label: 'Validée',           classes: 'bg-teal/10 text-teal border border-teal/25' },
  PENDING_EXIT:          { label: 'En attente',        classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  REJECTED_EXIT:         { label: 'Rejetée',           classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  // Orders
  ORDERED:               { label: 'Commandé',          classes: 'bg-blue-100 text-blue-700 border border-blue-200' },
  PARTIALLY_RECEIVED:    { label: 'Partiellement reçu', classes: 'bg-orange-400/10 text-orange-600 border border-orange-400/25' },
  WAITING_PROFORMA:      { label: 'Attente proforma',  classes: 'bg-yellow-400/10 text-yellow-700 border border-yellow-400/25' },
  PROFORMA_RECEIVED:     { label: 'Proforma reçue',    classes: 'bg-blue-100 text-blue-700 border border-blue-200' },
  // Conditions
  GOOD:                  { label: 'Bon état',          classes: 'bg-teal/10 text-teal border border-teal/25' },
  DAMAGED:               { label: 'Endommagé',         classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
  CONFORME:              { label: 'Conforme',          classes: 'bg-teal/10 text-teal border border-teal/25' },
  NON_CONFORME:          { label: 'Non conforme',      classes: 'bg-red-500/10 text-red-600 border border-red-500/25' },
}

const SIZE_CLASSES = {
  sm: 'text-[9px] px-2 py-0.5',
  md: 'text-[10px] px-2.5 py-1',
  lg: 'text-xs px-3 py-1.5',
}

const entry = STATUS_MAP[props.status]
const displayLabel = props.label || (entry ? entry.label : props.status)
const colorClasses = entry ? entry.classes : 'bg-offwhite-dim text-navy/60 border border-[#D0CABC]'
const sizeClasses = SIZE_CLASSES[props.size] || SIZE_CLASSES.md
</script>

<template>
  <span
    :class="[
      'inline-flex items-center font-medium uppercase tracking-wide rounded-pill',
      colorClasses,
      sizeClasses
    ]"
  >
    {{ displayLabel }}
  </span>
</template>
